import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { TAGLINE } from '../constants/brand';

const isElectron = typeof window !== 'undefined' && !!window.electronAPI;

const SECURITY_QUESTIONS = [
  "What was the name of your first pet?",
  "What city were you born in?",
  "What is your mother's maiden name?",
  "What was the name of your primary school?",
  "What was the make of your first car?",
];

const Register = () => {

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState('organizer');
  const [phone, setPhone] = useState('');
  const [securityQuestion, setSecurityQuestion] = useState(SECURITY_QUESTIONS[0]);
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  // Registration is disabled in the desktop app — accounts are created on the web.
  useEffect(() => {
    if (isElectron) navigate('/login', { replace: true });
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) return setError('Passwords do not match');
    if (password.length < 8) return setError('Password must be at least 8 characters');
    setLoading(true);
    try {
      await register({
        name, email, password, role,
        phone: role === 'organizer' ? phone : undefined,
        security_question: securityQuestion,
        security_answer: securityAnswer,
      });
      navigate('/tournaments');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: 'calc(100vh - 56px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem 1rem',
        background: 'linear-gradient(160deg, #f0fdfa 0%, #CCFBF1 50%, #f8fafc 100%)',
      }}
    >
      <div
        style={{
          width: '100%', maxWidth: 460,
          background: 'white', borderRadius: 16, padding: '2.5rem',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0',
        }}
      >
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div
            style={{
              width: 44, height: 44, borderRadius: 12,
              background: 'linear-gradient(135deg, #0D9488 0%, #0F766E 100%)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.25rem', color: 'white', fontWeight: 700, marginBottom: '0.75rem',
            }}
          >
            T
          </div>
          <img src="/TournNextTeal-WithoutTagline.svg" alt="TournNext" style={{ height: 32, width: 'auto', display: 'block', margin: '0 auto 0.5rem' }} />
          <p style={{ fontSize: '0.72rem', color: '#9ca3af', fontStyle: 'italic', margin: '0 auto 1.25rem', textAlign: 'center' }}>{TAGLINE}</p>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', margin: 0 }}>
            Create your account
          </h2>
          <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: 4 }}>
            Start managing tournaments today
          </p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input type="text" className="form-input" value={name} onChange={e => setName(e.target.value)} required placeholder="Your name" />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input type="email" className="form-input" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@example.com" />
          </div>
          <div className="form-group">
            <label className="form-label">I want to</label>
            <select className="form-select" value={role} onChange={e => setRole(e.target.value)} required>
              <option value="organizer">Create Tournaments (Organizer)</option>
              <option value="player">Play Tournaments (Player)</option>
            </select>
            <p style={{ color: '#9ca3af', fontSize: '0.78rem', marginTop: 4 }}>
              Choose Organizer to create tournaments, or Player to participate.
            </p>
          </div>

          {role === 'organizer' && (
            <div className="form-group">
              <label className="form-label">
                Phone Number <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span>
              </label>
              <input type="tel" className="form-input" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+91 98765 43210" />
            </div>
          )}

          <div className="grid grid-2">
            <div className="form-group">
              <label className="form-label">Password</label>
              <input type="password" className="form-input" value={password} onChange={e => setPassword(e.target.value)} required placeholder="Min 8 characters" minLength={8} />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <input type="password" className="form-input" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required placeholder="Repeat password" />
            </div>
          </div>

          <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '1rem', marginTop: '0.25rem' }}>
            <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.75rem' }}>
              Set a security question to recover your account if you forget your password.
            </p>
            <div className="form-group">
              <label className="form-label">Security Question</label>
              <select className="form-select" value={securityQuestion} onChange={e => setSecurityQuestion(e.target.value)} required>
                {SECURITY_QUESTIONS.map(q => <option key={q} value={q}>{q}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Security Answer</label>
              <input type="text" className="form-input" value={securityAnswer} onChange={e => setSecurityAnswer(e.target.value)} required placeholder="Your answer" autoComplete="off" />
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%', padding: '10px', fontSize: '0.95rem', marginTop: '0.25rem' }}
            disabled={loading}
          >
            {loading ? 'Creating account...' : 'Create account'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: '1.25rem', color: '#6b7280', fontSize: '0.875rem' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: '#0D9488', fontWeight: 500, textDecoration: 'none' }}>
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
